import { t } from 'i18next';
import { HTMLAttributes, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Close } from '../../../assets/img/storage/actions-close.svg';
import { ReactComponent as CopyNotify } from '../../../assets/img/storage/actions-copy.svg';
import { ReactComponent as UploadNotify } from '../../../assets/img/storage/actions-dl.svg';
import { ReactComponent as ReNotify } from '../../../assets/img/storage/actions-move.svg';
import { ReactComponent as WarnIcon } from '../../../assets/img/storage/icon-operations-warning.svg';
import { ReactComponent as ZipNotify } from '../../../assets/svg/ExtractAndStay.svg';
import { ReactComponent as Tools } from '../../../assets/svg/Tools.svg';
import { useUploadContext } from '../../../contexts/UploadContext';
import { SdnOperationTaskStatus, SdnOperationType, useDeleteSdnOperationMutation } from '../../../services/sdn';
import { ModuleTaskStatus } from '../../../services/transcode';

interface StorageNotificationItemProps {
    icon: React.ReactNode,
    name?: string,
    progress: number[],
    onClose: () => void
    onClick?: () => void
}

export function StorageNotificationItem({ icon, name, progress, onClose, onClick }: StorageNotificationItemProps) {
    return (
        <div className="storage__notifications-item" onClick={onClick}>
            <div className="storage__notifications-item-icon">
                {icon}
            </div>
            {name && <div className="storage__notifications-item-name">{name}</div>}
            <div className="storage__notifications-item-sep">·</div>
            <div className="storage__notifications-item-progress">{progress.length > 1 ? progress.length : (progress[0] >= 0 ? (progress[0] === 1000 ? 1 : `${progress[0].toFixed(1)}%`) : <WarnIcon />)}</div>
            <div className="storage__notifications-item-cancel" onClick={(e) => { e.stopPropagation(); onClose(); }}>
                <Close />
            </div>
        </div>
    )
}

interface StorageNotificationsProps extends HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode,
}

function StorageNotifications({ ...props }: StorageNotificationsProps) {
    const { children } = props;
    const navigate = useNavigate();
    const [deleteOperation] = useDeleteSdnOperationMutation();

    const { dispatch, state, operations, tasks, dispatchOperations } = useUploadContext();

    const onCloseUpdate = useCallback(() => {
        const ret = confirm(t('storage:DELETE_UPLOADS_CONFIRM'));
        if (ret) {
            state.filter((o) => !o.finished).forEach((s) => {
                s.abortController?.abort('handle');
                dispatch({ ...s, type: 'deleted' });
            });
        }
    }, [state]);

    return (
        <div className="storage__notifications">
            {state.filter((o) => !o.finished).length > 0 &&
                <StorageNotificationItem
                    icon={<UploadNotify />}
                    name={t('Upload')}
                    progress={state.filter((o) => !o.finished).map((s) => s.progress || 0)}
                    onClick={() => navigate('/sdn/operations/upload')}
                    onClose={onCloseUpdate}
                />}
            {operations.filter((o) => o.operation === SdnOperationType.COPY && o.status < SdnOperationTaskStatus.RETRY).length > 0
                && <StorageNotificationItem
                    icon={<CopyNotify />}
                    progress={operations.filter((o) => o.operation === SdnOperationType.COPY && o.status < SdnOperationTaskStatus.RETRY).map((o) => o.progress || 0)}
                    onClick={() => navigate('/sdn/operations/copy')}
                    onClose={() => operations.filter((o) => o.operation === SdnOperationType.COPY && o.status < SdnOperationTaskStatus.RETRY).map((o) => {
                        deleteOperation(o.id); dispatchOperations({ type: 'deleted', ...o })
                    })}
                />}
            {operations.filter((o) => o.operation === SdnOperationType.MOVE && o.status < SdnOperationTaskStatus.RETRY).length > 0
                && <StorageNotificationItem
                    icon={<ReNotify />}
                    progress={operations.filter((o) => o.operation === SdnOperationType.MOVE && o.status < SdnOperationTaskStatus.RETRY).map((o) => o.progress || 0)}
                    onClick={() => navigate('/sdn/operations/move')}
                    onClose={() => operations.filter((o) => o.operation === SdnOperationType.MOVE && o.status < SdnOperationTaskStatus.RETRY).map((o) => {
                        deleteOperation(o.id); dispatchOperations({ type: 'deleted', ...o })
                    })}
                />}
            {operations.filter((o) => o.operation === SdnOperationType.RENAME && o.status < SdnOperationTaskStatus.RETRY).length > 0
                && <StorageNotificationItem
                    icon={<ReNotify />}
                    name={t('storage:OPERATION_RENAME')}
                    progress={operations.filter((o) => o.operation === SdnOperationType.RENAME && o.status < SdnOperationTaskStatus.RETRY).map((o) => o.progress || 0)}
                    onClick={() => navigate('/sdn/operations/rename')}
                    onClose={() => operations.filter((o) => o.operation === SdnOperationType.RENAME && o.status < SdnOperationTaskStatus.RETRY).map((o) => {
                        deleteOperation(o.id); dispatchOperations({ type: 'deleted', ...o })
                    })}
                />}
            {operations.filter((o) => o.operation === SdnOperationType.UNZIP && o.status < SdnOperationTaskStatus.RETRY).length > 0
                && <StorageNotificationItem
                    icon={<ZipNotify />}
                    name={t('storage:OPERATION_UNZIP')}
                    progress={operations.filter((o) => o.operation === SdnOperationType.UNZIP && o.status < SdnOperationTaskStatus.RETRY).map((o) => o.progress || 0)}
                    onClick={() => navigate('/sdn/operations/unzip')}
                    onClose={() => operations.filter((o) => o.operation === SdnOperationType.UNZIP && o.status < SdnOperationTaskStatus.RETRY).map((o) => {
                        deleteOperation(o.id); dispatchOperations({ type: 'deleted', ...o })
                    })}
                />}
            {operations.filter((o) => o.operation === SdnOperationType.ZIP && o.status < SdnOperationTaskStatus.RETRY).length > 0
                && <StorageNotificationItem
                    icon={<ZipNotify />}
                    name={t('storage:OPERATION_ZIP')}
                    progress={operations.filter((o) => o.operation === SdnOperationType.ZIP && o.status < SdnOperationTaskStatus.RETRY).map((o) => o.progress || 0)}
                    onClick={() => navigate('/sdn/operations/zip')}
                    onClose={() => operations.filter((o) => o.operation === SdnOperationType.ZIP && o.status < SdnOperationTaskStatus.RETRY).map((o) => {
                        deleteOperation(o.id); dispatchOperations({ type: 'deleted', ...o })
                    })}
                />}
            {tasks.filter((o) => o.status < ModuleTaskStatus.COMPLETED).length > 0 &&
                <StorageNotificationItem
                    icon={<Tools />}
                    name={t('Transcoding')}
                    progress={tasks.filter((o) => o.status < ModuleTaskStatus.COMPLETED).map(() => 1000)}
                    onClick={() => navigate('/tools')}
                    onClose={() => navigate('/tools')}
                />}
            {children}
        </div>
    );
}

export default StorageNotifications;
